import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, KeyRound } from "lucide-react";
import { isAuthRetryableFetchError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/redefinir-senha")({
  head: () => ({
    meta: [{ title: "Redefinir Senha — Loja EG Elétrica" }, { name: "robots", content: "noindex" }],
  }),
  component: RedefinirSenhaPage,
});

function RedefinirSenhaPage() {
  const navigate = useNavigate();
  const [checkingLink, setCheckingLink] = useState(true);
  const [validLink, setValidLink] = useState(false);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // O link do e-mail troca o token pela sessão automaticamente
    // (detectSessionInUrl); só precisamos confirmar que a sessão existe.
    supabase.auth.getSession().then(({ data }) => {
      setValidLink(!!data.session);
      setCheckingLink(false);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setError("A senha precisa ter pelo menos 8 caracteres.");
      return;
    }
    setSubmitting(true);
    setError("");
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (error) {
      // Mesmo raciocínio de login.tsx/recuperar-senha.tsx: falha de
      // transporte/config não é o mesmo problema que um link expirado.
      setError(
        isAuthRetryableFetchError(error)
          ? "Tivemos um problema técnico e não conseguimos processar seu pedido agora. Tente novamente em alguns minutos."
          : "Não foi possível redefinir sua senha. Tente pedir um novo link.",
      );
      return;
    }
    navigate({ to: "/" });
  };

  if (checkingLink) {
    return (
      <div className="mx-auto max-w-sm px-4 py-12 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-eg-gold" />
      </div>
    );
  }

  if (!validLink) {
    return (
      <div className="mx-auto max-w-sm px-4 py-12 text-center">
        <h1 className="font-display text-2xl font-bold text-eg-dark mb-3">
          Link inválido ou expirado
        </h1>
        <p className="text-sm text-muted-foreground mb-4">
          Peça um novo link de recuperação de senha.
        </p>
        <Link
          to="/recuperar-senha"
          className="text-sm font-medium text-eg-goldtext hover:underline"
        >
          Recuperar senha
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-sm px-4 py-12">
      <h1 className="font-display text-2xl font-bold text-eg-dark mb-6">Redefinir Senha</h1>
      <form
        onSubmit={handleSubmit}
        className="space-y-4 rounded-lg bg-white border border-border p-6"
      >
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-eg-dark">Nova senha</span>
          <input
            type="password"
            required
            minLength={8}
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-eg-gold px-4 py-2.5 font-semibold text-eg-dark hover:bg-eg-goldhover disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <KeyRound className="h-5 w-5" />
          )}
          Salvar nova senha
        </button>
      </form>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.375rem;
          border: 1px solid var(--border);
          background: white;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .input:focus {
          border-color: var(--eg-gold);
          box-shadow: 0 0 0 2px color-mix(in oklab, var(--eg-gold) 30%, transparent);
        }
      `}</style>
    </div>
  );
}
